import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetOrdersOrderIdParams, TypeGetOrdersOrderIdResponse } from '~/types/delivery-agency.types';

/**
 * [배송 주문 관리] 배송 주문 상세 조회 API
 */
const fetchOrdersOrderId = async (
  params: TypeGetOrdersOrderIdParams
): Promise<TypeGetOrdersOrderIdResponse['data']> => {
  const { data } = await axiosInstance.get<TypeGetOrdersOrderIdResponse>(
    `${API_ENDPOINT.DELIVERY_AGENCY.GET_ORDER_DETAIL}/${params.orderId}`
  );
  return data.data;
};

/**
 * [배송 주문 관리] 배송 주문 상세 조회 Mutation Hook
 */
export const useGetOrdersOrderIdMutation = () => {
  return useMutation({
    mutationFn: fetchOrdersOrderId,
  });
};

/**
 * [배송 주문 관리] 배송 주문 상세 조회 Mock
 *
 * updatable(서버 판정)에 따라 수정 팝업(Case1, true) / 수정 불가 안내 팝업(Case2, false)으로 분기된다.
 */
registerMockAPI<TypeGetOrdersOrderIdResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: '/api-delivery-agency/orders/:id(\\d+)',
  label: '배송 주문 관리/주문 상세 조회',
  scenarios: [
    {
      name: '수정 가능 (출고 대기, updatable=true)',
      delay: 300,
      response: {
        data: {
          outboundCode: 'OB20260709001',
          orderStatus: { code: 'OUTBOUND_READY', description: '출고 대기' },
          updatable: true,
          orderInfoOutput: {
            ordererName: '김*리',
            receiverName: '김*리',
            receiverContact: '010-****-1234',
            sendAlimTalkImmediately: true,
            primaryAddress: '서울특별시 강남구 테헤란로 ***',
            secondaryAddress: '1**호',
            deliveryLocation: { code: 'DELIVERY_BOX', description: '택배 수령실' },
            deliveryLocationDetail: '지하 1층 무인 택배함 3번',
            entranceMethod: { code: 'PUBLIC_DOOR_PWD', description: '공동현관 비밀번호' },
            entranceMethodDetail: '#1234*',
          },
          collectingHistories: [
            {
              collectingRequestCode: 'RR20260804001',
              collectingRequestedDate: '2026-08-04',
              collectingRequestStatus: { code: 'COLLECT_ACCEPTED', description: '회수 예정' },
            },
            {
              collectingRequestCode: 'RR20260810002',
              collectingRequestedDate: '2026-08-10',
              collectingRequestStatus: { code: 'COLLECT_COMPLETED', description: '회수 완료' },
            },
          ],
          productNames: ['유기농 사과 1kg'],
          shippingLabelInfoOutputs: [
            {
              shippingLabelNumber: 'NM12345678-0001',
              shippingLabelStatus: { code: 'OUTBOUND_PROCESSING', description: '출고 진행중' },
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              courierCode: { code: 'NM', description: '넥스트마일' },
              statusUpdatedAt: '2026-07-09T10:00:00',
            },
          ],
        },
      },
    },
    {
      name: '수정 불가 (배송 중, updatable=false)',
      delay: 300,
      response: {
        data: {
          outboundCode: 'OB20260709002',
          orderStatus: { code: 'ON_THE_WAY', description: '배송 중' },
          updatable: false,
          orderInfoOutput: {
            ordererName: '김*리',
            receiverName: '김*리',
            receiverContact: '010-****-5678',
            sendAlimTalkImmediately: false,
            primaryAddress: '서울특별시 송파구 ***',
            secondaryAddress: '2**호',
            deliveryLocation: { code: 'DOORSTEP', description: '문 앞' },
            deliveryLocationDetail: null,
            entranceMethod: { code: 'CALL_SECURITY', description: '경비실 호출' },
            entranceMethodDetail: null,
          },
          collectingHistories: [],
          productNames: ['유기농 사과 1kg'],
          shippingLabelInfoOutputs: [
            {
              shippingLabelNumber: 'NM87654321-0001',
              shippingLabelStatus: { code: 'ON_THE_WAY', description: '배송 중' },
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              courierCode: { code: 'NM', description: '넥스트마일' },
              statusUpdatedAt: '2026-07-09T05:30:00',
            },
          ],
        },
      },
    },
    {
      name: '주문 없음 (60404)',
      status: 404,
      response: {
        error: {
          code: 60404,
          message: '요청한 리소스를 찾을 수 없습니다.',
        },
      },
    },
  ],
});
